import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { isSecretClas } from '@libs/postTools'
import { useTranslation } from '@i18n'

import Author from './components/AMP/Author'
import ImageCarousel from './components/AMP/ImageCarousel'
import BottomBar from './components/AMP/BottomBar'
import Article from './components/AMP/Article'
import ArticleCard from './components/AMP/ArticleCard'
import CommentSection from './components/AMP/CommentSection'
import { H1 } from './components/WebPost.styled'
import PopTime from './components/PopTime'
import Control from './components/AMP/Control'
import ContentBlock from './components/AMP/ContentBlock'

import config from '@public-config'
import { apiUrl } from '@libs/appUrl'
import { useCleanPostData } from '@features/post/contexts/CleanPostDataContext'
import { getCurrentPagePropertyFor } from '@libs/eventTracking/eventPropertyUtils'
import { createTrackArticleFromArticle } from '@app-core/eventTracking/article/articleUtil'
import { EventNames } from '@app-core/eventTracking/ArticleEvents'
import LayoutContent from '@components/LayoutContent'

const ImageBlock = styled.div`
  position: relative;
`

const PostContainer = styled.div`
  background: #fff;
  margin-bottom: 10px;
  border-radius: 4px;
  padding: 8px 0;
  font-size: 1.1em;
  line-height: 1.8em;
  word-wrap: break-word;
  user-select: text;

  ${H1} {
    margin-block-end: 12px;
    padding: 0 20px;
  }
`

const H2 = styled.h2`
  color: #58595b;
  margin: 20px 10px;
  font-weight: 700;
`

const PopTimeContainer = styled.div`
  padding: 15px 20px;
  font-size: 0.8em;
  color: #ababab;
`

function PostAmpContainer({ eventCatgories = [] }) {
  const { t } = useTranslation('post')
  const { post, title, description: content, url, nodes } = useCleanPostData()
  const readMore = useSelector<any, any>((state) =>
    state.post
      .getIn(['post', 'randomMore'])
      .filter((post) => !post.get('clas').includes('ad'))
      .toJS(),
  )
  const random = useSelector<any, any>((state) =>
    state.post
      .getIn(['post', 'random'])
      .filter((post) => !post.get('clas').includes('ad'))
      .toJS(),
  )
  const comments = useSelector<any, any>((state) => state.post.getIn(['post', 'comments']).toJS())

  const { postID, userInfo, loc, locName, img, t: time, secretColor } = post
  const isTrend = postID.includes('trend')

  const fetchPostViewScript = `
    try {
      // 用途解釋參見 web/src/features/post/index.tsx#L37
      const postID = '${postID}'
      setTimeout(() => {
        try {
          fetch('${apiUrl('/creator/share/pv')}', {
            method: 'POST',
            body: JSON.stringify({ postID }),
            headers: { 'Content-type': 'application/json' },
          })
        } catch (error) {
          console.warn(error)
        }
      }, 5000)
      // AMP 會輸出成靜態網頁沒有登入狀態，需額外拿 guest token
      fetch('${apiUrl('/login/guest')}')
        .then((response) => response.json())
        .then(({ token }) =>
          fetch('${apiUrl('/post/view')}', {
            method: 'POST',
            body: JSON.stringify({ postID, token, platform: 'mweb' }),
            headers: { 'Content-type': 'application/json' },
          }),
        )
    } catch (error) {
      console.warn(error)
    }
  `

  const amplitudePageViewScript = JSON.stringify({
    vars: {
      apiKey: config.amplitudeApiKeyV2,
    },
    triggers: {
      pageView: {
        on: 'visible',
        request: 'event',
        extraUrlParams: {
          event: {
            event_type: EventNames.ARTICLE_VIEW,
            event_properties: {
              article: createTrackArticleFromArticle(post),
              currentPage: getCurrentPagePropertyFor('article', `${url}?amp=1`),
              isAmpPage: true,
            },
          },
        },
      },
    },
  })

  const randomArticleCards = random.map((post) => <ArticleCard key={post.postID} {...post} />)

  const { contentType, authorRoles } = post
  const authorType = authorRoles?.join(',')
  const customDimension = JSON.stringify({ vars: { authorType, contentType } })
  const comscoreCode = JSON.stringify({
    vars: { c2: '19787903' },
    extraUrlParams: { comscorekw: 'amp' },
  })
  const gtagCode = JSON.stringify({
    vars: {
      gtag_id: 'G-28NKVZ4963',
      linker: { domains: ['popdaily.com.tw'] },
      config: { 'G-28NKVZ4963': { groups: 'default' } },
    },
  })
  const isSecret = isSecretClas(post.clas) // 匿名聊貼文
  const hasCoverImage = img[0] && img[0].path
  /**
   * 沒有首圖的匿名聊貼文
   * 才會顯示「背景純文字模式」
   */
  const isBackgroundTextPost = isSecret && !hasCoverImage

  const renderImageBlock = () => {
    if (hasCoverImage) {
      return (
        <ImageBlock>
          <ImageCarousel img={img} />
          <Control url={url} />
        </ImageBlock>
      )
    }

    if (isBackgroundTextPost) {
      return (
        <ImageBlock>
          <ContentBlock title={title} secretColor={secretColor} content={content} />
          )
          <Control url={url} />
        </ImageBlock>
      )
    }
  }
  return (
    <LayoutContent hasNavigation eventCatgories={eventCatgories}>
      {/* AdSense Auto Ads */}
      <amp-auto-ads type="adsense" data-ad-client="ca-pub-4054396321663077" />
      {/* Google Tag Manager */}
      <amp-analytics config={config.ampAnalyticsConfig} data-credentials="include">
        <script type="application/json" dangerouslySetInnerHTML={{ __html: customDimension }} />
      </amp-analytics>
      <amp-analytics type="amplitude" id="amplitude">
        <script
          type="application/json"
          dangerouslySetInnerHTML={{ __html: amplitudePageViewScript }}
        />
      </amp-analytics>
      <amp-analytics type="comscore">
        <script type="application/json" dangerouslySetInnerHTML={{ __html: comscoreCode }} />
      </amp-analytics>
      <amp-analytics type="gtag">
        <script type="application/json" dangerouslySetInnerHTML={{ __html: gtagCode }} />
      </amp-analytics>
      <PostContainer>
        {title && <H1>{title}</H1>}
        {renderImageBlock()}
        <Author userInfo={userInfo} loc={loc} locName={locName} url={url} />
        {!isBackgroundTextPost && <Article nodes={nodes} post={post} />}
        <PopTimeContainer>
          <PopTime time={time} />
        </PopTimeContainer>
      </PostContainer>
      <CommentSection url={url} comments={comments} />
      <amp-ad
        width="100vw"
        height="320"
        type="adsense"
        data-ad-client="ca-pub-4054396321663077"
        data-ad-slot="7721244596"
        data-auto-format="mcrspv"
        data-full-width=""
      />
      <aside>
        <H2>更多本作者貼文</H2>
        {randomArticleCards}
        {readMore.length !== 0 && (
          <>
            <H2>{t(isSecret ? 'post:更多精選話題' : 'post:其他同類型文章')}</H2>
            {readMore.map((post) => (
              <ArticleCard key={post.postID} {...post} />
            ))}
          </>
        )}
      </aside>
      {!isTrend && <BottomBar post={post} url={url} />}
      <amp-script nodom="" script="pv" />
      <script
        id="pv"
        type="text/plain"
        target="amp-script"
        dangerouslySetInnerHTML={{ __html: fetchPostViewScript }}
      />
    </LayoutContent>
  )
}

export default PostAmpContainer
